import { useState } from 'react';
import { useCurrentCalendarColor } from '../contexts/CurrentCalendarContext';

export type DeleteScope = 'all' | 'future' | 'this';

interface Props {
  eventTitle: string;
  occurrenceDate?: string;
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (scope: DeleteScope) => void;
  isDeleting?: boolean;
}

export default function DeleteRecurringEventDialog({
  eventTitle,
  occurrenceDate,
  isOpen,
  onCancel,
  onConfirm,
  isDeleting = false,
}: Props) {
  const accentColor = useCurrentCalendarColor();
  const [scope, setScope] = useState<DeleteScope>('this');

  if (!isOpen) return null;

  const formattedDate = occurrenceDate
    ? new Date(occurrenceDate).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <div className="space-y-4">
      <p className="font-mono text-sm text-ink-700">
        <span className="font-semibold">{eventTitle}</span> is a recurring event.
        {formattedDate && (
          <span className="text-ink-500">
            {' '}
            (This occurrence: {formattedDate})
          </span>
        )}
      </p>

      <div className="space-y-2">
        <label className="flex items-start gap-2 cursor-pointer p-2 rounded-sm hover:bg-paper-100">
          <input
            type="radio"
            name="delete-scope"
            value="this"
            checked={scope === 'this'}
            onChange={() => setScope('this')}
            className="mt-0.5 accent-[var(--accent-color)]"
            style={{ accentColor }}
          />
          <div>
            <span className="font-mono text-sm text-ink-700">This event only</span>
            <p className="font-mono text-xs text-ink-400">
              Other occurrences will remain on the calendar.
            </p>
          </div>
        </label>

        <label className="flex items-start gap-2 cursor-pointer p-2 rounded-sm hover:bg-paper-100">
          <input
            type="radio"
            name="delete-scope"
            value="future"
            checked={scope === 'future'}
            onChange={() => setScope('future')}
            className="mt-0.5 accent-[var(--accent-color)]"
            style={{ accentColor }}
          />
          <div>
            <span className="font-mono text-sm text-ink-700">This and all future events</span>
            <p className="font-mono text-xs text-ink-400">
              Past occurrences will remain on the calendar.
            </p>
          </div>
        </label>

        <label className="flex items-start gap-2 cursor-pointer p-2 rounded-sm hover:bg-paper-100">
          <input
            type="radio"
            name="delete-scope"
            value="all"
            checked={scope === 'all'}
            onChange={() => setScope('all')}
            className="mt-0.5 accent-[var(--accent-color)]"
            style={{ accentColor }}
          />
          <div>
            <span className="font-mono text-sm text-ink-700">All events</span>
            <p className="font-mono text-xs text-ink-400">
              All occurrences will be removed from the calendar.
            </p>
          </div>
        </label>
      </div>

      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="btn-ghost flex-1"
          disabled={isDeleting}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onConfirm(scope)}
          disabled={isDeleting}
          className="flex-1 bg-red-600 text-white px-4 py-2 rounded-sm text-sm font-mono hover:bg-red-700 transition-colors disabled:opacity-40"
        >
          {isDeleting ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </div>
  );
}
